import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import DeleteIcon from "@mui/icons-material/Delete";
import { DataGrid } from "@mui/x-data-grid";
import toast, { Toaster } from "react-hot-toast";
import * as services from "../../services/saleng";

const formatNumber = (value) =>
  Number(value || 0).toLocaleString("th-TH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

export default function Sell() {
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState([]);
  const [customerName, setCustomerName] = useState("");
  const [loading, setLoading] = useState(true);

  const loadStock = useCallback(async () => {
    setLoading(true);
    try {
      const [productsResponse, transactionsResponse] = await Promise.all([
        services.getProducts(),
        services.getBranchTransaction(),
      ]);
      const transactions = transactionsResponse.data?.data || [];
      const stockByProduct = new Map();

      transactions.forEach((transaction) => {
        const multiplier = transaction.transactionType === "SELL" ? -1 : 1;
        (transaction.items || []).forEach((item) => {
          stockByProduct.set(
            item.productId,
            (stockByProduct.get(item.productId) || 0) + Number(item.weight || 0) * multiplier
          );
        });
      });

      setProducts(
        (productsResponse.data?.data || [])
          .map((product) => ({ ...product, availableWeight: stockByProduct.get(product.id) || 0 }))
          .filter((product) => product.availableWeight > 0)
      );
    } catch (error) {
      console.error("Unable to load stock for sale:", error);
      toast.error("โหลดคลังสินค้าไม่สำเร็จ");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStock();
  }, [loadStock]);

  const addToCart = (product) => {
    if (cart.some((item) => item.productId === product.id)) return;
    setCart((current) => [
      ...current,
      {
        id: product.id,
        productId: product.id,
        productName: product.title,
        availableWeight: product.availableWeight,
        weight: "",
        pricePerUnit: "",
      },
    ]);
  };

  const updateCartItem = (id, field, value) => {
    if (value !== "" && !/^\d*\.?\d*$/.test(value)) return;
    setCart((current) =>
      current.map((item) => {
        if (item.id !== id) return item;
        if (field === "weight" && Number(value) > item.availableWeight) {
          return { ...item, weight: String(item.availableWeight) };
        }
        return { ...item, [field]: value };
      })
    );
  };

  const totalAmount = useMemo(
    () => cart.reduce((sum, item) => sum + Number(item.weight || 0) * Number(item.pricePerUnit || 0), 0),
    [cart]
  );

  const totalWeight = useMemo(
    () => cart.reduce((sum, item) => sum + Number(item.weight || 0), 0),
    [cart]
  );

  const saveSale = async () => {
    if (!cart.length) {
      toast.error("กรุณาเลือกสินค้าที่ต้องการขาย");
      return;
    }
    if (cart.some((item) => Number(item.weight) <= 0 || Number(item.pricePerUnit) < 0)) {
      toast.error("กรุณากรอกน้ำหนักและราคาขายให้ถูกต้อง");
      return;
    }

    try {
      await services.createTransaction({
        transactionId: window.crypto?.randomUUID?.() || `sale-${Date.now()}`,
        transactionType: "SELL",
        customerName: customerName.trim(),
        phoneNumber: null,
        line_uid: null,
        totalWeight,
        totalAmount,
        items: cart.map((item) => ({
          productName: item.productName,
          productId: item.productId,
          weight: Number(item.weight),
          weight_no_cal: Number(item.weight),
          deduct_weight: 0,
          moisture_weight: 0,
          impurity_weight: 0,
          car_weight: 0,
          other_weight: 0,
          pricePerUnit: Number(item.pricePerUnit),
          total: Number(item.weight) * Number(item.pricePerUnit),
        })),
      });
      toast.success("บันทึกรายการขายแล้ว");
      setCart([]);
      setCustomerName("");
      loadStock();
    } catch (error) {
      toast.error(error.response?.data?.message || "บันทึกรายการขายไม่สำเร็จ");
    }
  };

  const cartColumns = [
    { field: "productName", headerName: "สินค้า", flex: 1.5, minWidth: 160 },
    {
      field: "availableWeight",
      headerName: "ในคลัง (กก.)",
      width: 130,
      valueFormatter: (value) => formatNumber(value),
    },
    {
      field: "weight",
      headerName: "น้ำหนักขาย (กก.)",
      width: 160,
      renderCell: ({ row }) => (
        <TextField
          value={row.weight}
          onChange={(event) => updateCartItem(row.id, "weight", event.target.value)}
          inputProps={{ inputMode: "decimal", max: row.availableWeight }}
          size="small"
          fullWidth
        />
      ),
    },
    {
      field: "pricePerUnit",
      headerName: "ราคาขาย/กก.",
      width: 150,
      renderCell: ({ row }) => (
        <TextField
          value={row.pricePerUnit}
          onChange={(event) => updateCartItem(row.id, "pricePerUnit", event.target.value)}
          inputProps={{ inputMode: "decimal" }}
          size="small"
          fullWidth
        />
      ),
    },
    {
      field: "total",
      headerName: "รวม (บาท)",
      width: 120,
      valueGetter: (_value, row) => Number(row.weight || 0) * Number(row.pricePerUnit || 0),
      valueFormatter: (value) => formatNumber(value),
    },
    {
      field: "actions",
      headerName: "",
      width: 60,
      sortable: false,
      renderCell: ({ row }) => (
        <IconButton color="error" onClick={() => setCart((current) => current.filter((item) => item.id !== row.id))}>
          <DeleteIcon />
        </IconButton>
      ),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Toaster position="bottom-right" />
      <Typography variant="h5" fontWeight="bold" sx={{ mb: 2 }}>
        ขายสินค้า
      </Typography>
      <Stack direction={{ xs: "column", lg: "row" }} spacing={2}>
        <Paper sx={{ p: 2, flex: 1 }}>
          <Typography fontWeight="bold" sx={{ mb: 1 }}>เลือกสินค้าจากคลัง</Typography>
          <DataGrid
            rows={products}
            columns={[
              { field: "title", headerName: "สินค้า", flex: 1, minWidth: 180 },
              { field: "category", headerName: "หมวดหมู่", flex: 1, minWidth: 120 },
              { field: "availableWeight", headerName: "คงเหลือ (กก.)", width: 130, valueFormatter: (value) => formatNumber(value) },
              {
                field: "add",
                headerName: "",
                width: 70,
                sortable: false,
                renderCell: ({ row }) => (
                  <IconButton color="primary" onClick={() => addToCart(row)} aria-label="เพิ่มสินค้าขาย">
                    <AddShoppingCartIcon />
                  </IconButton>
                ),
              },
            ]}
            loading={loading}
            autoHeight
            disableRowSelectionOnClick
            pageSizeOptions={[10, 25, 50]}
            initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
          />
        </Paper>
        <Paper sx={{ p: 2, flex: 1.5 }}>
          <TextField label="ชื่อลูกค้า" value={customerName} onChange={(event) => setCustomerName(event.target.value)} fullWidth size="small" sx={{ mb: 2 }} />
          <DataGrid rows={cart} columns={cartColumns} autoHeight disableRowSelectionOnClick hideFooter />
          <Box sx={{ mt: 2, textAlign: "right" }}>
            <Typography>น้ำหนักขายรวม: {formatNumber(totalWeight)} กก.</Typography>
            <Typography variant="h6">ยอดขายรวม: {formatNumber(totalAmount)} บาท</Typography>
          </Box>
          <Button variant="contained" color="success" fullWidth sx={{ mt: 2 }} onClick={saveSale}>
            บันทึกรายการขาย
          </Button>
        </Paper>
      </Stack>
    </Box>
  );
}
